import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import ApiInspector from '../../../components/ApiInspector';
import PrintModal from '../../../components/PrintModal';
import { apiFetch } from '../../../lib/api';
import { useSocieta } from '../../../context/SocietaContext';
import { usePermesso } from '../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import { showToast } from '../../../components/Toast';
import { formatCurrency } from '../../../utils/formatters';
import { API_DOCS } from './OfferteCapitolatoDettaglioPage.api';
import PercorsoAcquirentiCard from './PercorsoAcquirentiCard';
import { API_DOCS as API_DOCS_PERCORSO } from './PercorsoAcquirentiCard.api';
import './OfferteCapitolatoDettaglioPage.css';

// La sezione Percorso acquirenti è un componente a parte, ma le sue chiamate partono da questa pagina:
// l'inspector deve conoscere anche i suoi endpoint. Array fuori dal componente: riferimento stabile tra i render.
const API_DOCS_PAGINA = [...API_DOCS, ...API_DOCS_PERCORSO];

// ── Icone ───────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const PrinterIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);
const ImportIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// ── Tipi ────────────────────────────────────────────────────────────────────

type StatoOfferta = 'BOZZA' | 'INVIATA_CLIENTE' | 'CONVERTITA_COMMESSA';
type TipoFatturazione = 'IMPRESA_PAGA_TUTTO' | 'CLIENTE_PAGA_DIRETTAMENTE';

interface SedeOption {
  id: number;
  nome: string;
}

interface RigaServer {
  id: number;
  ordine: number;
  prezzoUnitario: string;
  scorporo: string | null;
  descrizioneRiga: string | null;
  codicePrecopy: string;
  nomePrecopy: string;
  umVenditaPrecopy: string;
  articoloPreconfigId: number;
  articoloPreconfig: {
    id: number;
    codice: string;
    nome: string;
  };
}

interface RigaLocale {
  _key: string;
  id?: number;
  ordine: number;
  articoloPreconfigId: number;
  codicePrecopy: string;
  nomePrecopy: string;
  umVenditaPrecopy: string;
  prezzoUnitario: number;
  scorporo: number;
  descrizioneRiga: string;
}

interface RigaAccessoriaServer {
  id: number;
  ordine: number;
  articoloId: number;
  codicePrecopy: string;
  nomePrecopy: string;
  tipologiaPrecopy: string;
  umVenditaPrecopy: string;
  prezzoUnitario: string;
  scorporo: string | null;
  descrizioneRiga: string | null;
}

interface RigaAccessoriaLocale {
  _key: string;
  id?: number;
  ordine: number;
  articoloId: number;
  codicePrecopy: string;
  nomePrecopy: string;
  tipologiaPrecopy: string;
  umVenditaPrecopy: string;
  prezzoUnitario: number;
  scorporo: number;
  descrizioneRiga: string;
}

interface ArticoloOption {
  id: number;
  codice: string;
  descrizioneBase: string;
  tipologia: string;
  umVendita: { simbolo: string };
}

interface ArticoloPreconfigOption {
  id: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  nComponenti: number;
}

interface ClienteSelezionato {
  nominativoId: number;
  clienteNome: string;
  clienteIndirizzo: string;
  clienteCap: string;
  clienteCitta: string;
  clienteProvincia: string;
  clientePiva: string;
  clienteCf: string;
  condizionePagamentoId: number | null;
  condizionePagamentoNome: string | null;
}

interface CondizionePagamentoOption {
  id: number;
  nome: string;
  canale: string;
  societaId: number;
}

interface CapitolatoRigaPreconf {
  id: number;
  ordine: number;
  prezzoUnitario: string;
  descrizioneRiga: string | null;
  articoloPreconfig: {
    id: number;
    codice: string;
    nome: string;
    umVenditaSimbolo?: string;
  };
}

interface CapitolatoRigaAccessoriaImport {
  id: number;
  ordine: number;
  prezzoUnitario: string;
  descrizioneRiga: string | null;
  articolo: {
    id: number;
    codice: string;
    descrizioneBase: string;
    tipologia: string;
    umVendita: { simbolo: string };
  };
}

interface CapitolatoOption {
  id: number;
  codice: string;
  nome: string;
  righePrecConfigurate: CapitolatoRigaPreconf[];
  righeAccessorie: CapitolatoRigaAccessoriaImport[];
}

interface OffertaDettaglio {
  id: number;
  codice: string;
  societaId: number;
  stato: StatoOfferta;
  oggetto: string;
  testo: string | null;
  clienteNome: string;
  clienteIndirizzo: string | null;
  clienteCap: string | null;
  clienteCitta: string | null;
  clienteProvincia: string | null;
  clientePiva: string | null;
  clienteCf: string | null;
  nominativoId: number | null;
  sedeId: number | null;
  sede: { id: number; nome: string } | null;
  createdAt: string;
  createdBy: { id: number; nome: string } | null;
  ricarico: string | null;
  tipoFatturazione: TipoFatturazione | null;
  condizionePagamentoId: number | null;
  condizionePagamento: { id: number; nome: string; canale: string } | null;
  righe: RigaServer[];
  righeAccessorie: RigaAccessoriaServer[];
  commesse: { id: number; numero: string }[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const STATO_LABEL: Record<StatoOfferta, string> = {
  BOZZA: 'Bozza',
  INVIATA_CLIENTE: 'Inviata',
  CONVERTITA_COMMESSA: 'Convertita',
};

const STATO_CLS: Record<StatoOfferta, string> = {
  BOZZA: 'ofc-stato-badge--bozza',
  INVIATA_CLIENTE: 'ofc-stato-badge--inviata',
  CONVERTITA_COMMESSA: 'ofc-stato-badge--convertita',
};

// ── Modale ricerca articolo pre-configurato ─────────────────────────────────

function ModalRicercaPreconf({
  societaId,
  onClose,
  onSeleziona,
}: {
  societaId: string;
  onClose: () => void;
  onSeleziona: (a: ArticoloPreconfigOption) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArticoloPreconfigOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const cerca = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaId) params.set('societaId', societaId);
      if (q.trim()) params.set('q', q.trim());
      // @api GET /api/articoli-preconfigurati?societaId={id}&q={q} -> {"success":true,"data":[{"id":"number","codice":"string","nome":"string","umVenditaSimbolo":"string"}]}
      const res = await apiFetch<{ success: boolean; data: ArticoloPreconfigOption[] }>(
        `/api/articoli-preconfigurati?${params}`
      );
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [societaId]);

  useEffect(() => { cerca(''); }, [cerca]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => cerca(v), 300);
  };

  return (
    <div className="ofc-modal">
      <div className="ofc-modal__box ofc-modal__box--medium">
        <div className="ofc-modal__header">
          <h2 className="ofc-modal__title">Seleziona Articolo Pre-configurato</h2>
          <button className="ofc-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="ofc-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Cerca articolo</label>
            <input
              className="bop-input"
              value={query}
              onChange={handleChange}
              placeholder="Cerca per codice o nome..."
              autoFocus
            />
          </div>
          {loading && <div className="bop-empty-state"><span className="bop-text-muted">Caricamento...</span></div>}
          {!loading && results.length === 0 && <div className="bop-empty-state">Nessun articolo trovato</div>}
          {!loading && results.length > 0 && (
            <div className="ofc-search-dropdown">
              {results.map(a => (
                <div key={a.id} className="ofc-search-item" onClick={() => onSeleziona(a)}>
                  <div className="ofc-search-item__codice">{a.codice} — {a.nome}</div>
                  <div className="ofc-search-item__meta">
                    Padre: {a.articoloPadreCodice} | UM: {a.umVenditaSimbolo} | Componenti: {a.nComponenti}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="ofc-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale cerca cliente ────────────────────────────────────────────────────

function ModaleCercaCliente({
  societaId,
  onClose,
  onSeleziona,
}: {
  societaId: string;
  onClose: () => void;
  onSeleziona: (c: ClienteSelezionato) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const cerca = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('societaId', societaId);
      if (q.trim()) params.set('q', q.trim());
      // @api GET /api/nominativi/cerca-clienti?societaId={id}&q={q} -> {"success":true,"data":[{"id":"number","ragioneSociale":"string","nome":"string","cognome":"string","partitaIva":"string","codiceFiscale":"string","cliente":{"condizionePagamentoId":"number","condizionePagamento":{"id":"number","nome":"string","canale":"string"}}}]}
      const res = await apiFetch<{ success: boolean; data: any[] }>(
        `/api/nominativi/cerca-clienti?${params}`
      );
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [societaId]);

  useEffect(() => { if (societaId) cerca(''); }, [cerca, societaId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => cerca(v), 300);
  };

  const selectCliente = (n: any) => {
    const nome = n.ragioneSociale || [n.nome, n.cognome].filter(Boolean).join(' ');
    onSeleziona({
      nominativoId: n.id,
      clienteNome: nome,
      clienteIndirizzo: '',
      clienteCap: '',
      clienteCitta: '',
      clienteProvincia: '',
      clientePiva: n.partitaIva || '',
      clienteCf: n.codiceFiscale || '',
      condizionePagamentoId: n.cliente?.condizionePagamentoId ?? null,
      condizionePagamentoNome: n.cliente?.condizionePagamento?.nome ?? null,
    });
  };

  return (
    <div className="ofc-modal">
      <div className="ofc-modal__box ofc-modal__box--medium">
        <div className="ofc-modal__header">
          <h2 className="ofc-modal__title">Cerca Cliente</h2>
          <button className="ofc-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="ofc-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Cerca cliente</label>
            <input
              className="bop-input"
              value={query}
              onChange={handleChange}
              placeholder="Ragione sociale, nome..."
              autoFocus
            />
          </div>
          {loading && <div className="bop-empty-state"><span className="bop-text-muted">Caricamento...</span></div>}
          {!loading && results.length === 0 && <div className="bop-empty-state">Nessun cliente trovato</div>}
          {!loading && results.length > 0 && (
            <div className="ofc-search-dropdown">
              {results.map(n => (
                <div key={n.id} className="ofc-search-item" onClick={() => selectCliente(n)}>
                  <div className="ofc-search-item__codice">
                    {n.ragioneSociale || [n.nome, n.cognome].filter(Boolean).join(' ')}
                  </div>
                  <div className="ofc-search-item__meta">
                    {n.partitaIva ? `P.IVA: ${n.partitaIva}` : ''}
                    {n.codiceFiscale ? ` | CF: ${n.codiceFiscale}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="ofc-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale selezione articolo da anagrafica ───────────────────────────────

function ModalRicercaArticolo({
  societaId,
  onClose,
  onSeleziona,
}: {
  societaId: string;
  onClose: () => void;
  onSeleziona: (a: ArticoloOption) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArticoloOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const cerca = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaId) params.set('societaId', societaId);
      if (q.trim()) params.set('q', q.trim());
      // @api GET /api/anagrafiche/articoli?societaId={id}&q={q}&limit=50 -> {"success":true,"data":[{"id":"number","codice":"string","descrizioneBase":"string","tipologia":"string","umVendita":{"simbolo":"string"}}],"total":"number"}
      const res = await apiFetch<{ success: boolean; data: ArticoloOption[]; total: number }>(
        `/api/anagrafiche/articoli?${params}&limit=50`
      );
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [societaId]);

  useEffect(() => { cerca(''); }, [cerca]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => cerca(v), 300);
  };

  return (
    <div className="ofc-modal">
      <div className="ofc-modal__box ofc-modal__box--medium">
        <div className="ofc-modal__header">
          <div>
            <h2 className="ofc-modal__title">Seleziona Articolo</h2>
            <p className="ofc-modal__subtitle">Anagrafica articoli completa</p>
          </div>
          <button className="ofc-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="ofc-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Cerca articolo</label>
            <input
              className="bop-input"
              value={query}
              onChange={handleChange}
              placeholder="Cerca per codice o descrizione..."
              autoFocus
            />
          </div>
          {loading && <div className="bop-empty-state"><span className="bop-text-muted">Caricamento...</span></div>}
          {!loading && results.length === 0 && <div className="bop-empty-state">Nessun articolo trovato</div>}
          {!loading && results.length > 0 && (
            <div className="ofc-search-dropdown">
              {results.map(a => (
                <div key={a.id} className="ofc-search-item" onClick={() => onSeleziona(a)}>
                  <div className="ofc-search-item__codice">{a.codice} — {a.descrizioneBase}</div>
                  <div className="ofc-search-item__meta">
                    Tipologia: {a.tipologia} | UM: {a.umVendita?.simbolo || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="ofc-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale importa da capitolato ──────────────────────────────────────────

function ModaleImportaCapitolato({
  societaId,
  onClose,
  onImporta,
}: {
  societaId: string;
  onClose: () => void;
  onImporta: (righe: RigaLocale[], righeAccessorie: RigaAccessoriaLocale[]) => void;
}) {
  const [capitolati, setCapitolati] = useState<CapitolatoOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [dettaglio, setDettaglio] = useState<CapitolatoOption | null>(null);
  const [loadingDett, setLoadingDett] = useState(false);

  useEffect(() => {
    const carica = async () => {
      try {
        const params = new URLSearchParams();
        if (societaId) params.set('societaId', societaId);
        params.set('attivo', 'true');
        // @api GET /api/capitolati?societaId={id}&attivo=true -> {"success":true,"data":[{"id":"number","codice":"string","nome":"string"}]}
        const res = await apiFetch<{ success: boolean; data: CapitolatoOption[] }>(
          `/api/capitolati?${params}`
        );
        setCapitolati(res.data || []);
      } catch {
        setCapitolati([]);
      } finally {
        setLoading(false);
      }
    };
    carica();
  }, [societaId]);

  const handleSeleziona = async (id: number) => {
    setSelectedId(id);
    setLoadingDett(true);
    try {
      // @api GET /api/capitolati/:id -> {"success":true,"data":{"id":"number","righe":[]}}
      const res = await apiFetch<{ success: boolean; data: CapitolatoOption }>(
        `/api/capitolati/${id}`
      );
      setDettaglio(res.data);
    } catch {
      setDettaglio(null);
    } finally {
      setLoadingDett(false);
    }
  };

  const handleImporta = () => {
    if (!dettaglio) return;
    const nuoveRighe: RigaLocale[] = (dettaglio.righePrecConfigurate || []).map((r, idx) => ({
      _key: `imported-${r.id}-${idx}`,
      ordine: r.ordine,
      articoloPreconfigId: r.articoloPreconfig.id,
      codicePrecopy: r.articoloPreconfig.codice,
      nomePrecopy: r.articoloPreconfig.nome,
      umVenditaPrecopy: r.articoloPreconfig.umVenditaSimbolo || '',
      prezzoUnitario: parseFloat(r.prezzoUnitario),
      scorporo: 0,
      descrizioneRiga: r.descrizioneRiga || '',
    }));
    const nuoveAccessorie: RigaAccessoriaLocale[] = (dettaglio.righeAccessorie || []).map((r, idx) => ({
      _key: `imported-acc-${r.id}-${idx}`,
      ordine: r.ordine,
      articoloId: r.articolo.id,
      codicePrecopy: r.articolo.codice,
      nomePrecopy: r.articolo.descrizioneBase,
      tipologiaPrecopy: r.articolo.tipologia,
      umVenditaPrecopy: r.articolo.umVendita?.simbolo || '',
      prezzoUnitario: parseFloat(r.prezzoUnitario),
      scorporo: 0,
      descrizioneRiga: r.descrizioneRiga || '',
    }));
    onImporta(nuoveRighe, nuoveAccessorie);
  };

  return (
    <div className="ofc-modal">
      <div className="ofc-modal__box ofc-modal__box--medium">
        <div className="ofc-modal__header">
          <h2 className="ofc-modal__title">Importa da Capitolato</h2>
          <button className="ofc-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="ofc-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Seleziona capitolato</label>
            {loading ? (
              <div className="bop-text-muted">Caricamento...</div>
            ) : (
              <select
                className="bop-select"
                value={selectedId ?? ''}
                onChange={e => e.target.value ? handleSeleziona(parseInt(e.target.value, 10)) : setSelectedId(null)}
              >
                <option value="">— Seleziona —</option>
                {capitolati.map(c => (
                  <option key={c.id} value={c.id}>{c.codice} — {c.nome}</option>
                ))}
              </select>
            )}
          </div>
          {loadingDett && <div className="bop-text-muted">Caricamento righe...</div>}
          {dettaglio && !loadingDett && (
            <div className="ofc-import-preview">
              <p className="ofc-import-preview__label">{(dettaglio.righePrecConfigurate || []).length + (dettaglio.righeAccessorie || []).length} righe da importare</p>
              <div className="bop-card">
                <table className="bop-table">
                  <thead>
                    <tr><th>Codice</th><th>Nome</th><th>Prezzo</th></tr>
                  </thead>
                  <tbody>
                    {(dettaglio.righePrecConfigurate || []).map(r => (
                      <tr key={r.id}>
                        <td><span className="ofc-codice-badge">{r.articoloPreconfig.codice}</span></td>
                        <td>{r.articoloPreconfig.nome}</td>
                        <td>{formatCurrency(parseFloat(r.prezzoUnitario))}</td>
                      </tr>
                    ))}
                    {(dettaglio.righeAccessorie || []).map(r => (
                      <tr key={`acc-${r.id}`}>
                        <td><span className="ofc-codice-badge">{r.articolo.codice}</span></td>
                        <td>{r.articolo.descrizioneBase} <span className="bop-text-muted">(accessorio)</span></td>
                        <td>{formatCurrency(parseFloat(r.prezzoUnitario))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="ofc-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          <button
            className="bop-btn bop-btn-primary"
            onClick={handleImporta}
            disabled={!dettaglio}
          >
            Importa righe
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modale modifica riga (con scorporo) ─────────────────────────────────────

function ModalModificaRiga({
  riga,
  onClose,
  onSalva,
}: {
  riga: RigaLocale;
  onClose: () => void;
  onSalva: (r: RigaLocale) => void;
}) {
  const [prezzo, setPrezzo] = useState(String(riga.prezzoUnitario));
  const [scorporo, setScorporo] = useState(
    riga.scorporo > 0 ? String(riga.scorporo) : ''
  );
  const [descr, setDescr] = useState(riga.descrizioneRiga);

  const handleSalva = () => {
    const p = parseFloat(prezzo);
    if (isNaN(p) || p < 0) { showToast('Prezzo non valido', 'error'); return; }
    const s = scorporo !== '' ? parseFloat(scorporo) : 0;
    if (isNaN(s) || s < 0) { showToast('Scorporo non valido', 'error'); return; }
    onSalva({ ...riga, prezzoUnitario: p, scorporo: s, descrizioneRiga: descr });
  };

  return (
    <div className="ofc-modal">
      <div className="ofc-modal__box ofc-modal__box--medium">
        <div className="ofc-modal__header">
          <h2 className="ofc-modal__title">Modifica Riga</h2>
          <button className="ofc-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="ofc-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Articolo</label>
            <div className="ofc-riga-info">
              <strong>{riga.codicePrecopy}</strong> — {riga.nomePrecopy}
              <span className="bop-text-muted"> | UM: {riga.umVenditaPrecopy}</span>
            </div>
          </div>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Prezzo unitario capitolato *</label>
              <input
                className="bop-input"
                type="number"
                step="0.01"
                min="0"
                value={prezzo}
                onChange={e => setPrezzo(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">
                Scorporo
                <span className="ofc-label-hint"> (base calcolo extra)</span>
              </label>
              <input
                className="bop-input"
                type="number"
                step="0.01"
                min="0"
                value={scorporo}
                onChange={e => setScorporo(e.target.value)}
                placeholder="es. 35.00"
              />
            </div>
          </div>
          <div className="ofc-scorporo-info">
            <span className="ofc-scorporo-info__icon">i</span>
            <span>
              Lo scorporo e il valore base usato per calcolare la differenza
              extra-capitolato. Se un acquirente sceglie un prodotto a 60,00
              con scorporo 35,00, l'extra e 25,00 (non 60 - prezzo capitolato).
            </span>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Descrizione riga</label>
            <input
              className="bop-input"
              value={descr}
              onChange={e => setDescr(e.target.value)}
              placeholder="Descrizione opzionale..."
            />
          </div>
        </div>
        <div className="ofc-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={handleSalva}>Salva</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale modifica accessorio (con scorporo) ───────────────────────────────

function ModalModificaAccessorio({
  riga,
  onClose,
  onSalva,
}: {
  riga: RigaAccessoriaLocale;
  onClose: () => void;
  onSalva: (r: RigaAccessoriaLocale) => void;
}) {
  const [prezzo, setPrezzo] = useState(String(riga.prezzoUnitario));
  const [scorporo, setScorporo] = useState(
    riga.scorporo > 0 ? String(riga.scorporo) : ''
  );
  const [descr, setDescr] = useState(riga.descrizioneRiga);

  const handleSalva = () => {
    const p = parseFloat(prezzo);
    if (isNaN(p) || p < 0) { showToast('Prezzo non valido', 'error'); return; }
    const s = scorporo !== '' ? parseFloat(scorporo) : 0;
    if (isNaN(s) || s < 0) { showToast('Scorporo non valido', 'error'); return; }
    onSalva({ ...riga, prezzoUnitario: p, scorporo: s, descrizioneRiga: descr });
  };

  return (
    <div className="ofc-modal">
      <div className="ofc-modal__box ofc-modal__box--medium">
        <div className="ofc-modal__header">
          <h2 className="ofc-modal__title">Modifica Articolo Accessorio</h2>
          <button className="ofc-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="ofc-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Articolo</label>
            <div className="ofc-riga-info">
              <strong>{riga.codicePrecopy}</strong> — {riga.nomePrecopy}
              <span className="bop-text-muted"> | Tipologia: {riga.tipologiaPrecopy} | UM: {riga.umVenditaPrecopy}</span>
            </div>
          </div>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Prezzo unitario *</label>
              <input
                className="bop-input"
                type="number"
                step="0.01"
                min="0"
                value={prezzo}
                onChange={e => setPrezzo(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">
                Scorporo
                <span className="ofc-label-hint"> (base calcolo extra)</span>
              </label>
              <input
                className="bop-input"
                type="number"
                step="0.01"
                min="0"
                value={scorporo}
                onChange={e => setScorporo(e.target.value)}
                placeholder="es. 35.00"
              />
            </div>
          </div>
          <div className="ofc-scorporo-info">
            <span className="ofc-scorporo-info__icon">i</span>
            <span>
              Lo scorporo e il valore base usato per calcolare la differenza
              extra-capitolato. Se un acquirente sceglie un prodotto a 60,00
              con scorporo 35,00, l'extra e 25,00 (non 60 - prezzo capitolato).
            </span>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Descrizione riga</label>
            <input
              className="bop-input"
              value={descr}
              onChange={e => setDescr(e.target.value)}
              placeholder="Descrizione opzionale..."
            />
          </div>
        </div>
        <div className="ofc-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={handleSalva}>Salva</button>
        </div>
      </div>
    </div>
  );
}

// ── Autocomplete inline condizione di pagamento ─────────────────────────────

function AutocompleteCondizionePagamento({
  societaId,
  valueId,
  valueNome,
  disabled,
  onChange,
}: {
  societaId: string;
  valueId: number | null;
  valueNome: string | null;
  disabled: boolean;
  onChange: (id: number | null, nome: string | null) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CondizionePagamentoOption[]>([]);
  const [allOptions, setAllOptions] = useState<CondizionePagamentoOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!societaId) return;
    setLoading(true);
    // @api GET /api/nominativi/opzioni/condizioni-pagamento?societaId={id} -> {"success":true,"data":[{"id":"number","nome":"string","canale":"string","societaId":"number"}]}
    apiFetch<{ success: boolean; data: CondizionePagamentoOption[] }>(
      `/api/nominativi/opzioni/condizioni-pagamento?societaId=${societaId}`
    )
      .then(res => setAllOptions(res.data || []))
      .catch(() => setAllOptions([]))
      .finally(() => setLoading(false));
  }, [societaId]);

  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setResults(allOptions.slice(0, 20));
      return;
    }
    setResults(
      allOptions
        .filter(o => o.nome.toLowerCase().includes(q) || o.canale.toLowerCase().includes(q))
        .slice(0, 20)
    );
  }, [query, allOptions]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (valueId) {
    return (
      <div className="ofc-condpag-selected">
        <span className="ofc-condpag-badge">
          <span className="ofc-condpag-badge__icon">✓</span>
          <span className="ofc-condpag-badge__text">{valueNome || `#${valueId}`}</span>
          {!disabled && (
            <button
              type="button"
              className="ofc-condpag-badge__remove"
              title="Rimuovi"
              onClick={() => onChange(null, null)}
            >
              <XIcon />
            </button>
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="ofc-condpag-autocomplete" ref={rootRef}>
      <input
        className="bop-input"
        placeholder={loading ? 'Caricamento...' : 'Cerca condizione di pagamento...'}
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        disabled={disabled || loading}
      />
      {open && results.length > 0 && (
        <div className="ofc-condpag-dropdown">
          {results.map(o => (
            <div
              key={o.id}
              className="ofc-condpag-dropdown__item"
              onClick={() => {
                onChange(o.id, o.nome);
                setQuery('');
                setOpen(false);
              }}
            >
              <div className="ofc-condpag-dropdown__nome">{o.nome}</div>
              <div className="ofc-condpag-dropdown__meta">{o.canale}</div>
            </div>
          ))}
        </div>
      )}
      {open && !loading && results.length === 0 && (
        <div className="ofc-condpag-dropdown ofc-condpag-dropdown--empty">
          Nessuna condizione di pagamento trovata
        </div>
      )}
    </div>
  );
}

// ── Componente principale ───────────────────────────────────────────────────

export default function OfferteCapitolatoDettaglioPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { societaAttiva } = useSocieta();
  const { can } = usePermesso();

  const isNuova = id === 'nuovo';
  const offertaId = isNuova ? null : parseInt(id!, 10);
  const societaId = societaAttiva ? String(societaAttiva.id) : '';

  // State
  const [loading, setLoading] = useState(!isNuova);
  const [saving, setSaving] = useState(false);
  const [offertaOriginale, setOffertaOriginale] = useState<OffertaDettaglio | null>(null);
  const isConvertita = offertaOriginale?.stato === 'CONVERTITA_COMMESSA';
  const canEdit = can('commerciale.offerte_capitolato', 'edit') && !isConvertita;

  // Campi form
  const [oggetto, setOggetto] = useState('');
  const [testo, setTesto] = useState('');
  const [stato, setStato] = useState<StatoOfferta>('BOZZA');
  const [sedeId, setSedeId] = useState('');
  const [sediDisponibili, setSediDisponibili] = useState<SedeOption[]>([]);

  // Cliente
  const [clienteNome, setClienteNome] = useState('');
  const [clienteIndirizzo, setClienteIndirizzo] = useState('');
  const [clienteCap, setClienteCap] = useState('');
  const [clienteCitta, setClienteCitta] = useState('');
  const [clienteProvincia, setClienteProvincia] = useState('');
  const [clientePiva, setClientePiva] = useState('');
  const [clienteCf, setClienteCf] = useState('');
  const [nominativoId, setNominativoId] = useState<number | null>(null);

  // Nuovi campi
  const [ricarico, setRicarico] = useState('');
  const [tipoFatturazione, setTipoFatturazione] = useState<TipoFatturazione | ''>('');

  // Condizione di pagamento
  const [condizionePagamentoId, setCondizionePagamentoId] = useState<number | null>(null);
  const [condizionePagamentoNome, setCondizionePagamentoNome] = useState<string | null>(null);

  // Righe
  const [righe, setRighe] = useState<RigaLocale[]>([]);
  const [righeAccessorie, setRigheAccessorie] = useState<RigaAccessoriaLocale[]>([]);

  // Modali
  const [showRicercaPreconf, setShowRicercaPreconf] = useState(false);
  const [showRicercaArticolo, setShowRicercaArticolo] = useState(false);
  const [showImportaCapitolato, setShowImportaCapitolato] = useState(false);
  const [showCercaCliente, setShowCercaCliente] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [rigaInModifica, setRigaInModifica] = useState<RigaLocale | null>(null);
  const [rigaAccInModifica, setRigaAccInModifica] = useState<RigaAccessoriaLocale | null>(null);

  // Blocco cambio società mentre si è nella scheda
  useBloccoRecordContesto({
    recordSocietaId: offertaOriginale?.societaId,
    label: offertaOriginale
      ? `Offerta Capitolato ${offertaOriginale.codice}`
      : 'Offerta Capitolato',
  });

  // Mapping functions
  const mapRigheServer = (serverRighe: RigaServer[]): RigaLocale[] =>
    serverRighe.map((r, idx) => ({
      _key: String(r.id || idx),
      id: r.id,
      ordine: r.ordine,
      articoloPreconfigId: r.articoloPreconfigId,
      codicePrecopy: r.codicePrecopy,
      nomePrecopy: r.nomePrecopy,
      umVenditaPrecopy: r.umVenditaPrecopy,
      prezzoUnitario: parseFloat(String(r.prezzoUnitario)),
      scorporo: r.scorporo != null ? parseFloat(String(r.scorporo)) : 0,
      descrizioneRiga: r.descrizioneRiga || '',
    }));

  const mapRigheAccessorieServer = (serverRighe: RigaAccessoriaServer[]): RigaAccessoriaLocale[] =>
    serverRighe.map((r, idx) => ({
      _key: String(r.id || idx),
      id: r.id,
      ordine: r.ordine,
      articoloId: r.articoloId,
      codicePrecopy: r.codicePrecopy,
      nomePrecopy: r.nomePrecopy,
      tipologiaPrecopy: r.tipologiaPrecopy,
      umVenditaPrecopy: r.umVenditaPrecopy,
      prezzoUnitario: parseFloat(String(r.prezzoUnitario)),
      scorporo: r.scorporo != null ? parseFloat(String(r.scorporo)) : 0,
      descrizioneRiga: r.descrizioneRiga || '',
    }));

  // Carica sedi
  useEffect(() => {
    if (!societaId) return;
    // @api GET /api/sedi/mie?societaId={id}&commerciale=true -> {"success":true,"data":[{"id":"number","nome":"string"}]}
    apiFetch<{ success: boolean; data: SedeOption[] }>(
      `/api/sedi/mie?societaId=${societaId}&commerciale=true`
    ).then(res => setSediDisponibili(res.data || [])).catch(() => {});
  }, [societaId]);

  // Carica offerta esistente
  useEffect(() => {
    if (isNuova || !offertaId) return;
    setLoading(true);
    // @api GET /api/offerte-capitolato/:id -> {"success":true,"data":{"id":"number","codice":"string","ricarico":"string","tipoFatturazione":"string","condizionePagamentoId":"number","condizionePagamento":{"id":"number","nome":"string","canale":"string"},"righe":[{"scorporo":"string"}],"righeAccessorie":[{"scorporo":"string"}]}}
    apiFetch<{ success: boolean; data: OffertaDettaglio }>(
      `/api/offerte-capitolato/${offertaId}`
    ).then(res => {
      const o = res.data;
      setOffertaOriginale(o);
      setOggetto(o.oggetto || '');
      setTesto(o.testo || '');
      setStato(o.stato);
      setSedeId(o.sedeId ? String(o.sedeId) : '');
      setClienteNome(o.clienteNome || '');
      setClienteIndirizzo(o.clienteIndirizzo || '');
      setClienteCap(o.clienteCap || '');
      setClienteCitta(o.clienteCitta || '');
      setClienteProvincia(o.clienteProvincia || '');
      setClientePiva(o.clientePiva || '');
      setClienteCf(o.clienteCf || '');
      setNominativoId(o.nominativoId || null);
      setRicarico(o.ricarico != null ? String(o.ricarico) : '');
      setTipoFatturazione(o.tipoFatturazione || '');
      setCondizionePagamentoId(o.condizionePagamentoId ?? null);
      setCondizionePagamentoNome(o.condizionePagamento?.nome ?? null);
      setRighe(mapRigheServer(o.righe || []));
      setRigheAccessorie(mapRigheAccessorieServer(o.righeAccessorie || []));
    }).catch(e => {
      showToast(e.message || 'Errore caricamento', 'error');
      navigate('/commerciale/capitolati/offerte');
    }).finally(() => setLoading(false));
  }, [offertaId, isNuova, navigate]);

  // Handlers
  const handleSelezionaCliente = (c: ClienteSelezionato) => {
    setNominativoId(c.nominativoId);
    setClienteNome(c.clienteNome);
    setClienteIndirizzo(c.clienteIndirizzo);
    setClienteCap(c.clienteCap);
    setClienteCitta(c.clienteCitta);
    setClienteProvincia(c.clienteProvincia);
    setClientePiva(c.clientePiva);
    setClienteCf(c.clienteCf);
    // Sovrascrittura silenziosa: sempre si allinea al cliente
    setCondizionePagamentoId(c.condizionePagamentoId);
    setCondizionePagamentoNome(c.condizionePagamentoNome);
    setShowCercaCliente(false);
  };

  const handleAggiungiArticolo = (a: ArticoloPreconfigOption) => {
    const nuovaRiga: RigaLocale = {
      _key: `new-${Date.now()}-${Math.random()}`,
      ordine: righe.length,
      articoloPreconfigId: a.id,
      codicePrecopy: a.codice,
      nomePrecopy: a.nome,
      umVenditaPrecopy: a.umVenditaSimbolo || '',
      prezzoUnitario: 0,
      scorporo: 0,
      descrizioneRiga: '',
    };
    setRighe(prev => [...prev, nuovaRiga]);
    setShowRicercaPreconf(false);
  };

  const handleImportaRighe = (nuoveRighe: RigaLocale[], nuoveAccessorie: RigaAccessoriaLocale[]) => {
    setRighe(prev => [
      ...prev,
      ...nuoveRighe.map((r, idx) => ({ ...r, ordine: prev.length + idx })),
    ]);
    setRigheAccessorie(prev => [
      ...prev,
      ...nuoveAccessorie.map((r, idx) => ({ ...r, ordine: prev.length + idx })),
    ]);
    setShowImportaCapitolato(false);
  };

  const handleRimuoviRiga = (_key: string) => {
    setRighe(prev => prev.filter(r => r._key !== _key).map((r, idx) => ({ ...r, ordine: idx })));
  };

  const handleAggiungiAccessorio = (a: ArticoloOption) => {
    const nuova: RigaAccessoriaLocale = {
      _key: `acc-${Date.now()}-${Math.random()}`,
      ordine: righeAccessorie.length,
      articoloId: a.id,
      codicePrecopy: a.codice,
      nomePrecopy: a.descrizioneBase,
      tipologiaPrecopy: a.tipologia,
      umVenditaPrecopy: a.umVendita?.simbolo || '',
      prezzoUnitario: 0,
      scorporo: 0,
      descrizioneRiga: '',
    };
    setRigheAccessorie(prev => [...prev, nuova]);
    setShowRicercaArticolo(false);
  };

  const handleRimuoviAccessorio = (_key: string) => {
    setRigheAccessorie(prev => prev.filter(r => r._key !== _key).map((r, idx) => ({ ...r, ordine: idx })));
  };

  const handleSalvaRiga = (r: RigaLocale) => {
    setRighe(prev => prev.map(row => row._key === r._key ? r : row));
    setRigaInModifica(null);
  };

  const handleSalvaAccessorio = (r: RigaAccessoriaLocale) => {
    setRigheAccessorie(prev => prev.map(row => row._key === r._key ? r : row));
    setRigaAccInModifica(null);
  };

  const handleSalva = async () => {
    if (!oggetto.trim()) { showToast('Oggetto e obbligatorio', 'error'); return; }
    if (!clienteNome.trim()) { showToast('Nome cliente e obbligatorio', 'error'); return; }
    if (!societaId && isNuova) { showToast('Seleziona una societa', 'error'); return; }
    setSaving(true);
    try {
      const body = {
        ...(isNuova ? { societaId: parseInt(societaId, 10) } : {}),
        sedeId: sedeId ? parseInt(sedeId, 10) : null,
        oggetto: oggetto.trim(),
        testo: testo.trim() || null,
        stato,
        clienteNome: clienteNome.trim(),
        clienteIndirizzo: clienteIndirizzo.trim() || null,
        clienteCap: clienteCap.trim() || null,
        clienteCitta: clienteCitta.trim() || null,
        clienteProvincia: clienteProvincia.trim() || null,
        clientePiva: clientePiva.trim() || null,
        clienteCf: clienteCf.trim() || null,
        nominativoId,
        ricarico: ricarico !== '' ? parseFloat(ricarico) : null,
        tipoFatturazione: tipoFatturazione || null,
        condizionePagamentoId,
        righe: righe.map((r, idx) => ({
          articoloPreconfigId: r.articoloPreconfigId,
          codicePrecopy: r.codicePrecopy,
          nomePrecopy: r.nomePrecopy,
          umVenditaPrecopy: r.umVenditaPrecopy,
          ordine: idx,
          prezzoUnitario: r.prezzoUnitario,
          scorporo: r.scorporo > 0 ? r.scorporo : null,
          descrizioneRiga: r.descrizioneRiga || null,
        })),
        righeAccessorie: righeAccessorie.map((r, idx) => ({
          articoloId: r.articoloId,
          codicePrecopy: r.codicePrecopy,
          nomePrecopy: r.nomePrecopy,
          tipologiaPrecopy: r.tipologiaPrecopy,
          umVenditaPrecopy: r.umVenditaPrecopy,
          ordine: idx,
          prezzoUnitario: r.prezzoUnitario,
          scorporo: r.scorporo > 0 ? r.scorporo : null,
          descrizioneRiga: r.descrizioneRiga || null,
        })),
      };

      if (isNuova) {
        // @api POST /api/offerte-capitolato {"societaId":"number","oggetto":"string","clienteNome":"string","ricarico":"number","tipoFatturazione":"string","condizionePagamentoId":"number","righe":[{"scorporo":"number"}]} -> {"success":true,"data":{"id":"number","codice":"string","condizionePagamentoId":"number","condizionePagamento":{"id":"number","nome":"string","canale":"string"}}}
        const res = await apiFetch<{ success: boolean; data: { id: number; codice: string } }>(
          '/api/offerte-capitolato', { method: 'POST', body: JSON.stringify(body) }
        );
        showToast('Offerta creata', 'success');
        navigate(`/commerciale/capitolati/offerte/${res.data.id}`);
      } else {
        // @api PUT /api/offerte-capitolato/:id {"oggetto":"string","ricarico":"number","tipoFatturazione":"string","condizionePagamentoId":"number","righe":[{"scorporo":"number"}]} -> {"success":true,"data":{"condizionePagamentoId":"number","condizionePagamento":{"id":"number","nome":"string","canale":"string"}}}
        await apiFetch(`/api/offerte-capitolato/${offertaId}`, {
          method: 'PUT', body: JSON.stringify(body)
        });
        showToast('Offerta aggiornata', 'success');
        // Ricarica i dati aggiornati
        const res2 = await apiFetch<{ success: boolean; data: OffertaDettaglio }>(
          `/api/offerte-capitolato/${offertaId}`
        );
        setOffertaOriginale(res2.data);
      }
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nel salvataggio', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Calcoli
  const totaleRighe = righe.reduce((sum, r) => sum + r.prezzoUnitario, 0);
  const totaleAccessorie = righeAccessorie.reduce((sum, r) => sum + r.prezzoUnitario, 0);

  // Guard: nuova offerta senza societa
  if (!societaId && isNuova) {
    return (
      <Layout>
        <div className="bop-empty-state">
          Seleziona una societa dalla topbar per creare una nuova offerta.
        </div>
      </Layout>
    );
  }

  // Loading
  if (loading) {
    return (
      <Layout>
        <div className="bop-empty-state">Caricamento...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="ofc-dettaglio-page">

        {/* Header pagina */}
        <div className="ofc-dettaglio-header">
          <div className="ofc-dettaglio-header__left">
            <BopBackLink to="/commerciale/capitolati/offerte" label="Torna alle offerte capitolato" />
            <h1 className="bop-page-title">
              {isNuova
                ? 'Nuova Offerta Capitolato'
                : offertaOriginale?.codice ?? 'Caricamento...'}
            </h1>
            {offertaOriginale && (
              <span className={`ofc-stato-badge ${STATO_CLS[offertaOriginale.stato]}`}>
                {STATO_LABEL[offertaOriginale.stato]}
              </span>
            )}
          </div>
          <div className="ofc-dettaglio-header__actions">
            {!isNuova && offertaOriginale && !isConvertita && (
              <button className="bop-btn bop-btn-secondary" onClick={() => setShowPrint(true)}>
                <PrinterIcon /> Stampa
              </button>
            )}
            {!isConvertita && (
              <button
                className="bop-btn bop-btn-secondary"
                onClick={() => navigate('/commerciale/capitolati/offerte')}
              >
                Annulla
              </button>
            )}
            {canEdit && (
              <button
                className="bop-btn bop-btn-primary"
                onClick={handleSalva}
                disabled={saving}
              >
                {saving ? 'Salvataggio...' : isNuova ? 'Crea offerta' : 'Salva modifiche'}
              </button>
            )}
          </div>
        </div>

        {isConvertita && (
          <div className="ofc-banner-convertita">
            <span>Offerta convertita in commessa - sola lettura.</span>
            {offertaOriginale?.commesse?.[0] && (
              <Link
                to={`/commerciale/commesse/${offertaOriginale.commesse[0].id}`}
                className="ofc-banner-convertita__link"
              >
                Vai alla commessa {offertaOriginale.commesse[0].numero} &rarr;
              </Link>
            )}
          </div>
        )}

        {/* SEZIONE: Dati cliente */}
        <div className="bop-card ofc-dettaglio-section">
          <div className="ofc-section-header">
            <span className="ofc-section-label">Dati cliente</span>
            {canEdit && (
              <button className="bop-btn bop-btn-secondary" onClick={() => setShowCercaCliente(true)}>
                <SearchIcon /> Cerca in anagrafica
              </button>
            )}
          </div>
          {nominativoId && (
            <div className="ofc-cliente-info">
              <span className="ofc-cliente-info__label">Cliente collegato ad anagrafica</span>
            </div>
          )}
          <div className="ofc-form-grid">
            <div className="bop-form-group ofc-form-grid--full">
              <label className="bop-label">Nome / Ragione Sociale *</label>
              <input className="bop-input" value={clienteNome}
                onChange={e => setClienteNome(e.target.value)} disabled={!canEdit}
                placeholder="Nome o ragione sociale..." />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Partita IVA</label>
              <input className="bop-input" value={clientePiva}
                onChange={e => setClientePiva(e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Codice Fiscale</label>
              <input className="bop-input" value={clienteCf}
                onChange={e => setClienteCf(e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group ofc-form-grid--full">
              <label className="bop-label">Indirizzo</label>
              <input className="bop-input" value={clienteIndirizzo}
                onChange={e => setClienteIndirizzo(e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">CAP</label>
              <input className="bop-input" value={clienteCap}
                onChange={e => setClienteCap(e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Citta</label>
              <input className="bop-input" value={clienteCitta}
                onChange={e => setClienteCitta(e.target.value)} disabled={!canEdit} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Provincia</label>
              <input className="bop-input" value={clienteProvincia}
                onChange={e => setClienteProvincia(e.target.value)}
                disabled={!canEdit} maxLength={2} />
            </div>
            <div className="bop-form-group ofc-form-grid--full">
              <label className="bop-label">
                Condizione di pagamento
                {nominativoId && (
                  <span className="ofc-label-hint"> (dall'anagrafica cliente — modificabile)</span>
                )}
              </label>
              <AutocompleteCondizionePagamento
                societaId={societaId}
                valueId={condizionePagamentoId}
                valueNome={condizionePagamentoNome}
                disabled={!canEdit}
                onChange={(id, nome) => {
                  setCondizionePagamentoId(id);
                  setCondizionePagamentoNome(nome);
                }}
              />
            </div>
          </div>
        </div>

        {/* SEZIONE: Dati offerta */}
        <div className="bop-card ofc-dettaglio-section">
          <div className="ofc-section-header">
            <span className="ofc-section-label">Dati offerta</span>
          </div>
          <div className="ofc-form-grid">
            <div className="bop-form-group ofc-form-grid--full">
              <label className="bop-label">Oggetto *</label>
              <input className="bop-input" value={oggetto}
                onChange={e => setOggetto(e.target.value)} disabled={!canEdit}
                placeholder="Oggetto dell'offerta..." />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Stato</label>
              <select className="bop-select" value={stato}
                onChange={e => setStato(e.target.value as StatoOfferta)} disabled={!canEdit}>
                <option value="BOZZA">Bozza</option>
                <option value="INVIATA_CLIENTE">Inviata al cliente</option>
                {!isNuova && <option value="CONVERTITA_COMMESSA" disabled>Convertita in commessa</option>}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Sede</label>
              <select className="bop-select" value={sedeId}
                onChange={e => setSedeId(e.target.value)} disabled={!canEdit}>
                <option value="">— Nessuna sede —</option>
                {sediDisponibili.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
            <div className="bop-form-group ofc-form-grid--full">
              <label className="bop-label">Testo / Note</label>
              <textarea className="bop-input" rows={3} value={testo}
                onChange={e => setTesto(e.target.value)} disabled={!canEdit}
                placeholder="Testo libero, note per il cliente..." />
            </div>
          </div>
        </div>

        {/* SEZIONE: Ricarichi impresa e regole di fatturazione */}
        <div className="bop-card ofc-dettaglio-section">
          <div className="ofc-section-header">
            <span className="ofc-section-label">Ricarichi impresa e regole di fatturazione</span>
          </div>

          <div className="ofc-info-box">
            <span className="ofc-info-box__icon">i</span>
            <div className="ofc-info-box__text">
              Questi parametri definiscono come vengono gestiti gli <strong>extra-capitolati</strong>,
              ovvero i prodotti che gli acquirenti dell'impresa scelgono al di fuori
              del capitolato base. Vengono trasferiti nella commessa e usati nelle
              offerte agli acquirenti.
            </div>
          </div>

          <div className="ofc-ricarico-section">
            <div className="ofc-ricarico-row">
              <div className="bop-form-group ofc-ricarico-input">
                <label className="bop-label">Ricarico impresa su extra-capitolati (%)</label>
                <div className="ofc-input-with-suffix">
                  <input
                    className="bop-input"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={ricarico}
                    onChange={e => setRicarico(e.target.value)}
                    disabled={!canEdit}
                    placeholder="es. 20.00"
                  />
                  <span className="ofc-input-suffix">%</span>
                </div>
              </div>
              {ricarico !== '' && parseFloat(ricarico) > 0 && (
                <div className="ofc-ricarico-example">
                  <span className="ofc-ricarico-example__label">Esempio:</span>
                  <span className="ofc-ricarico-example__text">
                    Extra di 20,00 &rarr; fatturato {formatCurrency(20 * (1 + parseFloat(ricarico) / 100))}
                    {' '}(margine impresa: {formatCurrency(20 * parseFloat(ricarico) / 100)})
                  </span>
                </div>
              )}
            </div>

            <div className="ofc-fatturazione-section">
              <label className="bop-label">Regola di fatturazione degli extra-capitolati</label>

              <div className="ofc-fatturazione-options">
                <label className={`ofc-fatturazione-option ${tipoFatturazione === 'IMPRESA_PAGA_TUTTO' ? 'ofc-fatturazione-option--selected' : ''} ${!canEdit ? 'ofc-fatturazione-option--disabled' : ''}`}>
                  <input
                    type="radio"
                    name="tipoFatturazione"
                    value="IMPRESA_PAGA_TUTTO"
                    checked={tipoFatturazione === 'IMPRESA_PAGA_TUTTO'}
                    onChange={() => canEdit && setTipoFatturazione('IMPRESA_PAGA_TUTTO')}
                    disabled={!canEdit}
                  />
                  <div className="ofc-fatturazione-option__content">
                    <div className="ofc-fatturazione-option__title">
                      L'impresa gestisce e incassa gli extra dal cliente finale
                    </div>
                    <div className="ofc-fatturazione-option__desc">
                      L'impresa applica il ricarico e incassa dal cliente finale il valore
                      degli extra-capitolati. A noi l'impresa riconosce il valore del
                      capitolato piu il costo dell'extra <strong>senza ricarico</strong>.
                    </div>
                  </div>
                </label>

                <label className={`ofc-fatturazione-option ${tipoFatturazione === 'CLIENTE_PAGA_DIRETTAMENTE' ? 'ofc-fatturazione-option--selected' : ''} ${!canEdit ? 'ofc-fatturazione-option--disabled' : ''}`}>
                  <input
                    type="radio"
                    name="tipoFatturazione"
                    value="CLIENTE_PAGA_DIRETTAMENTE"
                    checked={tipoFatturazione === 'CLIENTE_PAGA_DIRETTAMENTE'}
                    onChange={() => canEdit && setTipoFatturazione('CLIENTE_PAGA_DIRETTAMENTE')}
                    disabled={!canEdit}
                  />
                  <div className="ofc-fatturazione-option__content">
                    <div className="ofc-fatturazione-option__title">
                      Il cliente finale paga gli extra direttamente a noi
                    </div>
                    <div className="ofc-fatturazione-option__desc">
                      Il cliente finale paga a noi il prezzo extra maggiorato del ricarico.
                      Il maggiore incasso che realizziamo viene <strong>dedotto</strong> dal
                      valore del capitolato che l'impresa ci deve pagare.
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* SEZIONE: Righe articoli */}
        <div className="bop-card ofc-dettaglio-section">
          <div className="ofc-section-header">
            <span className="ofc-section-label">
              Righe articoli ({righe.length})
              {(righe.length > 0 || righeAccessorie.length > 0) && (
                <span className="ofc-righe-totale"> — Totale: {formatCurrency(totaleRighe + totaleAccessorie)}</span>
              )}
            </span>
            {canEdit && (
              <div className="bop-flex">
                <button
                  className="bop-btn bop-btn-secondary"
                  onClick={() => setShowImportaCapitolato(true)}
                >
                  <ImportIcon /> Da capitolato
                </button>
                <button
                  className="bop-btn bop-btn-secondary"
                  onClick={() => setShowRicercaPreconf(true)}
                >
                  <PlusIcon /> Aggiungi articolo
                </button>
              </div>
            )}
          </div>

          {righe.length === 0 ? (
            <div className="ofc-righe-empty">
              Nessuna riga. Usa "Aggiungi articolo" o "Da capitolato" per aggiungere articoli.
            </div>
          ) : (
            <div className="bop-card">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Codice</th>
                    <th>Nome</th>
                    <th>UM</th>
                    <th>Prezzo unit.</th>
                    <th>
                      Scorporo
                      <span className="ofc-th-hint" title="Valore base per il calcolo delta extra-capitolato">?</span>
                    </th>
                    <th>Descrizione</th>
                    {canEdit && <th>Azioni</th>}
                  </tr>
                </thead>
                <tbody>
                  {righe.map((r, idx) => (
                    <tr key={r._key}>
                      <td>{idx + 1}</td>
                      <td><span className="ofc-codice-badge">{r.codicePrecopy}</span></td>
                      <td>{r.nomePrecopy}</td>
                      <td>{r.umVenditaPrecopy}</td>
                      <td>{formatCurrency(r.prezzoUnitario)}</td>
                      <td>
                        {r.scorporo > 0
                          ? formatCurrency(r.scorporo)
                          : <span className="bop-text-muted">—</span>}
                      </td>
                      <td>{r.descrizioneRiga || <span className="bop-text-muted">—</span>}</td>
                      {canEdit && (
                        <td>
                          <div className="bop-flex">
                            <button
                              className="bop-btn bop-btn-icon"
                              title="Modifica"
                              onClick={() => setRigaInModifica(r)}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="bop-btn bop-btn-icon"
                              title="Rimuovi"
                              onClick={() => handleRimuoviRiga(r._key)}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* SEZIONE: Articoli accessori aggiuntivi */}
        <div className="bop-card ofc-dettaglio-section">
          <div className="ofc-section-header">
            <span className="ofc-section-label">
              Articoli accessori aggiuntivi ({righeAccessorie.length})
            </span>
            {canEdit && (
              <button
                className="bop-btn bop-btn-secondary"
                onClick={() => setShowRicercaArticolo(true)}
              >
                <PlusIcon /> Aggiungi accessorio
              </button>
            )}
          </div>

          {righeAccessorie.length === 0 ? (
            <div className="ofc-righe-empty">
              Nessun articolo accessorio aggiunto.
            </div>
          ) : (
            <div className="bop-card">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Codice</th>
                    <th>Nome</th>
                    <th>Tipologia</th>
                    <th>UM</th>
                    <th>Prezzo unit.</th>
                    <th>
                      Scorporo
                      <span className="ofc-th-hint" title="Valore base per il calcolo delta extra-capitolato">?</span>
                    </th>
                    <th>Descrizione</th>
                    {canEdit && <th>Azioni</th>}
                  </tr>
                </thead>
                <tbody>
                  {righeAccessorie.map((r, idx) => (
                    <tr key={r._key}>
                      <td>{idx + 1}</td>
                      <td><span className="ofc-codice-badge">{r.codicePrecopy}</span></td>
                      <td>{r.nomePrecopy}</td>
                      <td>{r.tipologiaPrecopy}</td>
                      <td>{r.umVenditaPrecopy}</td>
                      <td>{formatCurrency(r.prezzoUnitario)}</td>
                      <td>
                        {r.scorporo > 0
                          ? formatCurrency(r.scorporo)
                          : <span className="bop-text-muted">—</span>}
                      </td>
                      <td>{r.descrizioneRiga || <span className="bop-text-muted">—</span>}</td>
                      {canEdit && (
                        <td>
                          <div className="bop-flex">
                            <button
                              className="bop-btn bop-btn-icon"
                              title="Modifica"
                              onClick={() => setRigaAccInModifica(r)}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="bop-btn bop-btn-icon"
                              title="Rimuovi"
                              onClick={() => handleRimuoviAccessorio(r._key)}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!isNuova && offertaOriginale && (
          <PercorsoAcquirentiCard offertaId={offertaOriginale.id} codiceOfferta={offertaOriginale.codice} />
        )}

        <ApiInspector docs={API_DOCS_PAGINA} />
      </div>

      {/* Modali */}
      {showRicercaPreconf && (
        <ModalRicercaPreconf
          societaId={societaId}
          onClose={() => setShowRicercaPreconf(false)}
          onSeleziona={handleAggiungiArticolo}
        />
      )}

      {showRicercaArticolo && (
        <ModalRicercaArticolo
          societaId={societaId}
          onClose={() => setShowRicercaArticolo(false)}
          onSeleziona={handleAggiungiAccessorio}
        />
      )}

      {showImportaCapitolato && (
        <ModaleImportaCapitolato
          societaId={societaId}
          onClose={() => setShowImportaCapitolato(false)}
          onImporta={handleImportaRighe}
        />
      )}

      {showCercaCliente && (
        <ModaleCercaCliente
          societaId={societaId}
          onClose={() => setShowCercaCliente(false)}
          onSeleziona={handleSelezionaCliente}
        />
      )}

      {rigaInModifica && (
        <ModalModificaRiga
          riga={rigaInModifica}
          onClose={() => setRigaInModifica(null)}
          onSalva={handleSalvaRiga}
        />
      )}

      {rigaAccInModifica && (
        <ModalModificaAccessorio
          riga={rigaAccInModifica}
          onClose={() => setRigaAccInModifica(null)}
          onSalva={handleSalvaAccessorio}
        />
      )}

      {showPrint && offertaOriginale && (
        <PrintModal
          societaId={parseInt(societaId || '0', 10)}
          entitaType="offerta_capitolato"
          entitaId={offertaOriginale.id}
          entitaLabel={`${offertaOriginale.codice} — ${offertaOriginale.oggetto}`}
          onClose={() => setShowPrint(false)}
        />
      )}
    </Layout>
  );
}
